import { ensureLedgerMarkIndex, peekLedgerMarkIndex, setLedgerHoldingsPaperMode } from "./ledgerMarks";
import { loadLedgerCatalog, type LedgerAccount } from "./ownLedgerClient";

const STORAGE_KEY = "sfe-account-scope-v1";

export interface AccountScopeState {
  /** Kept for older saved scopes. The menu no longer filters by broker. */
  brokerIds: string[];
  /** Empty means every account in the current paper mode. */
  accountIds: string[];
  /** When true, only paper accounts are shown. Tax forces this off. */
  includePaper: boolean;
}

type ScopeSurface = "default" | "tax";

const listeners = new Set<() => void>();
let generation = 0;
let surface: ScopeSurface = "default";
let catalog: LedgerAccount[] = [];
let catalogReady = false;
let holdingsByTicker = new Map<string, string[]>();

const DEFAULT_STATE: AccountScopeState = {
  brokerIds: [],
  accountIds: [],
  includePaper: false,
};

/** True after the user toggles Live/Paper. An untouched scope can still open on Paper. */
let paperModeChosen = false;
let state: AccountScopeState = readStoredState();

function readStoredState(): AccountScopeState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw) as Partial<AccountScopeState> & { paperModeChosen?: boolean };
    paperModeChosen = parsed.paperModeChosen === true;
    return {
      brokerIds: [],
      accountIds: Array.isArray(parsed.accountIds) ? parsed.accountIds.map(String) : [],
      includePaper: parsed.includePaper === true,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function persist(): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, paperModeChosen }));
}

function emit(): void {
  generation += 1;
  setLedgerHoldingsPaperMode(paperAccountsEnabled());
  for (const listener of listeners) listener();
}

/** Changes whenever the account scope or tax paper lock changes. */
export function accountScopeGeneration(): number {
  return generation;
}

export function subscribeAccountScope(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function accountScopeState(): AccountScopeState {
  return state;
}

/** Tax never includes paper accounts, and the paper checkbox is locked. */
export function setAccountScopeSurface(tabId: string): void {
  const next: ScopeSurface = tabId === "tax" ? "tax" : "default";
  if (next === surface) return;
  surface = next;
  emit();
}

export function accountScopeSurface(): ScopeSurface {
  return surface;
}

export function paperAccountsEnabled(): boolean {
  return surface !== "tax" && state.includePaper;
}

const IRRELEVANT_SHORT_NAMES = new Set(["wbf", "wbk", "wbe"]);

/** Webull futures, crypto, and events cash are not wheel books. */
export function isIrrelevantScopeAccount(account: {
  label?: string | null;
  name?: string | null;
  short_name?: string | null;
}): boolean {
  const short = String(account.short_name ?? "").trim().toLowerCase();
  if (IRRELEVANT_SHORT_NAMES.has(short)) return true;
  const label = `${account.label ?? ""} ${account.name ?? ""}`.toLowerCase();
  return label.includes("crypto") || label.includes("futures") || label.includes("event");
}

export function isPaperLedgerAccount(account: {
  account_id?: string | null;
  broker_id?: string | null;
  label?: string | null;
}): boolean {
  const id = String(account.account_id ?? "").trim().toLowerCase();
  const broker = String(account.broker_id ?? "").trim().toLowerCase();
  const label = String(account.label ?? "").trim().toLowerCase();
  return (
    broker === "paper" ||
    id.startsWith("webull:paper:") ||
    id.startsWith("paper_agent") ||
    label.startsWith("paper")
  );
}

function refreshHoldings(): void {
  const index = peekLedgerMarkIndex();
  const next = new Map<string, string[]>();
  if (!index) {
    holdingsByTicker = next;
    return;
  }
  for (const [ticker, lots] of index.holdings) {
    next.set(
      ticker,
      lots.map((lot) => lot.accountId),
    );
  }
  holdingsByTicker = next;
}

export function accountScopeCatalog(): LedgerAccount[] {
  return catalog;
}

/** Paper books are the only ones with trades or cash. Empty seed accounts do not count. */
export function paperBooksAreTheOnlyActivity(
  accounts: Array<{
    account_id: string;
    broker_id?: string | null;
    label?: string | null;
    short_name?: string | null;
    name?: string | null;
  }>,
  activeIds: string[],
): boolean {
  const active = new Set(activeIds);
  const used = accounts.filter(
    (account) => active.has(account.account_id) && !isIrrelevantScopeAccount(account),
  );
  return used.length > 0 && used.every((account) => isPaperLedgerAccount(account));
}

export async function ensureAccountScopeCatalog(force = false): Promise<LedgerAccount[]> {
  if (catalogReady && !force) return catalog;
  try {
    const payload = await loadLedgerCatalog(force);
    catalog = payload.accounts ?? [];
    catalogReady = true;
    const hidden = irrelevantAccountIds();
    if (state.accountIds.some((id) => hidden.has(id))) {
      state = { ...state, accountIds: state.accountIds.filter((id) => !hidden.has(id)) };
      persist();
    }
    // A paper-only book opened on Live shows an empty seeded account and hides
    // the Webull paper cash and positions the user actually has.
    if (!paperModeChosen && paperBooksAreTheOnlyActivity(catalog, payload.active_account_ids ?? [])) {
      const paperIds = new Set(
        catalog
          .filter((account) => isPaperLedgerAccount(account) && !isIrrelevantScopeAccount(account))
          .map((account) => account.account_id),
      );
      const accountIds = state.accountIds.filter((id) => paperIds.has(id));
      if (!state.includePaper || accountIds.length !== state.accountIds.length) {
        state = { ...state, includePaper: true, accountIds };
        persist();
      }
    }
    await ensureLedgerMarkIndex(force).catch(() => null);
    refreshHoldings();
    emit();
  } catch {
    catalogReady = catalogReady || false;
  }
  return catalog;
}

function paperAccountIds(): Set<string> {
  return new Set(
    catalog.filter((account) => isPaperLedgerAccount(account)).map((account) => account.account_id),
  );
}

function irrelevantAccountIds(): Set<string> {
  return new Set(
    catalog.filter((account) => isIrrelevantScopeAccount(account)).map((account) => account.account_id),
  );
}

function accountInPaperMode(account: {
  account_id?: string | null;
  broker_id?: string | null;
  label?: string | null;
}): boolean {
  const paper = isPaperLedgerAccount(account);
  return paperAccountsEnabled() ? paper : !paper;
}

/** Accounts the current tab is allowed to show. Paper mode is paper accounts only. */
export function visibleScopeAccounts(): LedgerAccount[] {
  return catalog.filter((account) => accountInPaperMode(account) && !isIrrelevantScopeAccount(account));
}

function allowedAccountIds(): Set<string> | null {
  if (!catalogReady) return null;
  const pool = visibleScopeAccounts();
  const picked = new Set(state.accountIds);
  const rows = picked.size ? pool.filter((account) => picked.has(account.account_id)) : pool;
  return new Set(rows.map((account) => account.account_id));
}

function scopeIsNarrowed(): boolean {
  return state.accountIds.length > 0;
}

function idIsPaper(accountId: string, brokerId?: string | null): boolean {
  const row = catalog.find((account) => account.account_id === accountId);
  if (row) return isPaperLedgerAccount(row);
  return isPaperLedgerAccount({ account_id: accountId, broker_id: brokerId });
}

export function scopeAllowsAccount(
  accountId: string | null | undefined,
  brokerId?: string | null,
): boolean {
  const allowed = allowedAccountIds();
  if (!allowed) return true;
  const id = String(accountId ?? "").trim();
  if (!id) return !scopeIsNarrowed() && !paperAccountsEnabled();
  if (irrelevantAccountIds().has(id)) return false;
  if (paperAccountsEnabled() !== idIsPaper(id, brokerId)) return false;
  if (!scopeIsNarrowed()) return true;
  return allowed.has(id);
}

export function scopeAllowsTicker(ticker: string | null | undefined): boolean {
  const allowed = allowedAccountIds();
  if (!allowed) return true;
  const key = String(ticker ?? "").trim().toUpperCase();
  const lots = key ? holdingsByTicker.get(key) : undefined;
  const paperOn = paperAccountsEnabled();
  if (!lots?.length) return !paperOn && !scopeIsNarrowed();
  const paper = paperAccountIds();
  const hidden = irrelevantAccountIds();
  const modeLots = lots.filter((id) => !hidden.has(id) && (paperOn ? paper.has(id) : !paper.has(id)));
  if (!scopeIsNarrowed()) return modeLots.length > 0;
  return modeLots.some((id) => allowed.has(id));
}

/** Put ideas stay listed in paper mode. Paper books are often cash-only, so
 * requiring an open paper lot would hide the whole Puts screener. */
export function scopeAllowsRecommendation(
  ticker: string | null | undefined,
  side?: "put" | "call",
): boolean {
  if (side === "put" && paperAccountsEnabled()) return true;
  return scopeAllowsTicker(ticker);
}

export function updateAccountScope(patch: Partial<AccountScopeState>): void {
  paperModeChosen = true;
  const includePaper = patch.includePaper ?? state.includePaper;
  const paperOn = surface !== "tax" && includePaper;
  const selectable = new Set(
    catalog
      .filter(
        (account) =>
          !isIrrelevantScopeAccount(account) &&
          (paperOn ? isPaperLedgerAccount(account) : !isPaperLedgerAccount(account)),
      )
      .map((account) => account.account_id),
  );
  const accountIds = (patch.accountIds ?? state.accountIds).filter(
    (id) => !catalogReady || selectable.has(id),
  );
  state = { brokerIds: [], accountIds, includePaper };
  persist();
  emit();
}
